import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { supabase } from "../supabase";

export const WorkspaceContext = createContext();

// A helper to pull features for the current user (could be expanded later)

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return context;
};

export const WorkspaceProvider = ({ children }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  const [defaultWorkspaceId, setDefaultWorkspaceId] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [isTrialExpired, setIsTrialExpired] = useState(false);

  // Safe localStorage getters
  const safeGetItem = (key, defaultVal) => {
    const val = localStorage.getItem(key);
    return val && val !== "undefined" && val !== "null" ? val : defaultVal;
  };

  const [viewMode, setViewMode] = useState(safeGetItem("viewMode", "pro"));
  const [adminOverrideEnabled, setAdminOverrideEnabled] = useState(
    safeGetItem("adminOverrideEnabled", "false") === "true",
  );
  const [adminSimulatedTier, setAdminSimulatedTier] = useState(
    safeGetItem("adminSimulatedTier", "Super Admin"),
  );
  const [hasAgencyPlan, setHasAgencyPlan] = useState(false);

  const hasFeature = (featureKey) => {
      // Super admins always have access to everything, or we can just rely on the features object
      if (userRole === 'super_admin' && adminOverrideEnabled) return true;
      return !!userFeatures[featureKey];
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(undefined);

  const [userFeatures, setUserFeatures] = useState({});

  const userRoleRef = useRef(undefined);
  const userRolePromiseRef = useRef(null);
  const isFetchingRef = useRef(false);

  // Workspace Limits State
  const [workspaceLimit, setWorkspaceLimit] = useState(-1); // Unlimited for all plans
  const [campaignLimit, setCampaignLimit] = useState(1); // Default to 1 (Free Trial)
  const [memberLimit, setMemberLimit] = useState(1); // Default to 1 (Free Trial)
  const [funnelLimit, setFunnelLimit] = useState(1); // Default to 1
  const [workspacesLeft, setWorkspacesLeft] = useState(0);

  // Helper to fetch/get role
  const getUserRole = async () => {
    // 1. Check cache (Implements caching via userRoleRef to prevent redundant API calls)
    if (userRoleRef.current !== undefined) return userRoleRef.current;

    // 2. Check in-flight request
    if (userRolePromiseRef.current) return userRolePromiseRef.current;

    // 3. Create new request
    userRolePromiseRef.current = (async () => {
      try {
        // Use getSession instead of getUser for performance (local check vs remote)
        const {
          data: { session },
        } = await supabase.auth.getSession();

        // If no session, cache null to prevent repeated checks.
        // onAuthStateChange will clear this cache when user logs in.
        if (!session?.user) {
          userRoleRef.current = null;
          setUserRole(null);
          return null;
        }

        const [roleRes, featuresRes] = await Promise.all([
          supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", session.user.id)
            .maybeSingle(),
          supabase
            .from("user_features")
            .select("features")
            .eq("user_id", session.user.id)
            .maybeSingle()
        ]);

        if (roleRes.error) {
          console.error("Error fetching user role:", roleRes.error);
          // Don't cache errors, allow retry
          return null;
        }

        if (featuresRes.error) {
          console.error("Error fetching user features:", featuresRes.error);
        }

        const role = roleRes.data?.role || null;
        userRoleRef.current = role;
        setUserRole(role);
        setUserFeatures(featuresRes.data?.features || {});
        return role;
      } finally {
        userRolePromiseRef.current = null;
      }
    })();

    return userRolePromiseRef.current;
  };

  useEffect(() => {
    const lastActiveWorkspaceId = localStorage.getItem("activeWorkspaceId");
    fetchWorkspaces(lastActiveWorkspaceId);

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN") {
        userRoleRef.current = undefined; // Clear cache on new sign in
        setUserRole(undefined);
        const lastId = localStorage.getItem("activeWorkspaceId");
        fetchWorkspaces(lastId, true); // true = refresh in background
      } else if (event === "SIGNED_OUT") {
        setWorkspaces([]);
        setActiveWorkspace(null);
        userRoleRef.current = undefined;
        setUserRole(undefined);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Update limits whenever workspaces change

  useEffect(() => {
    // Workspaces are now unlimited
    setWorkspacesLeft(-1);
  }, [workspaces, workspaceLimit]);

  // Listen to realtime subscription updates for the active workspace
  useEffect(() => {
    if (activeWorkspace) {
      const channel = supabase
        .channel(`subscriptions_channel_${activeWorkspace.id}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "subscriptions",
            filter: `workspace_id=eq.${activeWorkspace.id}`,
          },
          (payload) => {
            console.log("Subscription changed:", payload);
            checkSubscription(activeWorkspace);
          },
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [activeWorkspace]);

  // Update limits and subscription status whenever admin override changes
  useEffect(() => {
    // Only re-check if we have an active workspace and the user is an admin
    // We don't want to fetch workspaces repeatedly while loading
    if (activeWorkspace && userRole === "super_admin" && !loading) {
      checkSubscription(activeWorkspace);
      // We might also need to call fetchWorkspaces to update limits
      // but fetchWorkspaces sets isFetchingRef to true and might abort if already fetching
      // Let's call fetchWorkspaces with background=true
      fetchWorkspaces(activeWorkspace.id, true);
    }
  }, [adminOverrideEnabled, adminSimulatedTier]);

  const fetchWorkspaces = async (lastActiveId = null, background = false) => {
    // Prevent concurrent fetches/creations to avoid race conditions and duplicate workspaces
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      // Only set loading if not a background refresh and no data exists
      if (!background && workspaces.length === 0) {
        setLoading(true);
      }
      setError(null);

      // Start fetching role early to parallelize requests
      const rolePromise = getUserRole();

      // 1. Fetch Workspaces
      const { data, error } = await supabase
        .from("workspaces")
        .select("*")
        .order("name");

      if (error) {
        // Check for missing table error
        if (
          error.code === "42P01" ||
          error.code === "PGRST205" ||
          error.message?.includes("does not exist")
        ) {
          console.warn("Supabase tables not found. Please run migrations.");
          setError({ ...error, type: "MISSING_TABLES" });
        } else {
          throw error;
        }
        setWorkspaces([]);
        return;
      }

      setWorkspaces(data);

      // 2. Determine Limits (Look for best subscription across all workspaces)
      // Since subscriptions are per workspace, we assume if *any* workspace has a plan with higher limits,
      // it applies to the user/account.
      // Note: In a real multi-tenant app, limits might be strictly enforced by the "owner" account or specific subscription.
      // Here we take the most generous limit found.
      let maxWorkspaceLimit = -1; // Unlimited
      let maxCampaignLimit = 1; // Default Free Trial
      let maxMemberLimit = 1; // Default Free Trial
      let maxFunnelLimit = 1; // Default Free Trial
      let userHasAgency = false;

      // If we have workspaces, fetch their subscriptions
      if (data && data.length > 0) {
        const workspaceIds = data.map((ws) => ws.id);

        const { data: subs, error: subError } = await supabase
          .from("subscriptions")
          .select("plan_name, workspace_id")
          .in("workspace_id", workspaceIds);

        if (!subError && subs && subs.length > 0) {
          // Check for agency plan
          for (const sub of subs) {
            if ((sub.plan_name || '').toLowerCase().includes("agency")) {
              userHasAgency = true;
            }
          }

          // Fetch plan details to get gates
          // We need to match plan_name from subscriptions to name in subscription_plans
          // Note: subscriptions table has plan_name, subscription_plans has name.

          const planNames = [...new Set(subs.map((s) => s.plan_name))];

          const { data: plans, error: planError } = await supabase
            .from("subscription_plans")
            .select("name, gates")
            .in("name", planNames);

          if (!planError && plans) {
            plans.forEach((plan) => {
              // Workspaces Limit
              maxWorkspaceLimit = -1;
              // Campaigns Limit
              if (plan.gates && plan.gates.max_campaigns !== undefined) {
                const limit = plan.gates.max_campaigns;
                if (limit === -1) {
                  maxCampaignLimit = -1; // Unlimited
                } else if (
                  maxCampaignLimit !== -1 &&
                  limit > maxCampaignLimit
                ) {
                  maxCampaignLimit = limit;
                }
              }
              // Members Limit
              if (plan.gates && plan.gates.max_members !== undefined) {
                const limit = plan.gates.max_members;
                if (limit === -1) {
                  maxMemberLimit = -1; // Unlimited
                } else if (maxMemberLimit !== -1 && limit > maxMemberLimit) {
                  maxMemberLimit = limit;
                }
              }
              // Funnels Limit
              if (plan.gates && plan.gates.max_funnels !== undefined) {
                const limit = plan.gates.max_funnels;
                if (limit === -1) {
                  maxFunnelLimit = -1; // Unlimited
                } else if (maxFunnelLimit !== -1 && limit > maxFunnelLimit) {
                  maxFunnelLimit = limit;
                }
              }
            });
          }
        }
      }

      // Check for Super Admin override (Applied AFTER subscription checks to ensure priority)
      const role = await rolePromise;
      if (
        (role === "super_admin" && adminOverrideEnabled) ||
        role === "lifetime"
      ) {
        if (adminSimulatedTier === "Super Admin") {
          maxWorkspaceLimit = -1;
          maxCampaignLimit = -1;
          maxMemberLimit = -1;
          maxFunnelLimit = -1;
        } else if (adminSimulatedTier === "Agency") {
          maxWorkspaceLimit = -1;
          maxCampaignLimit = -1;
          maxMemberLimit = -1;
          maxFunnelLimit = -1;
        } else if (adminSimulatedTier === "Pro") {
          maxWorkspaceLimit = -1;
          maxCampaignLimit = 10;
          maxMemberLimit = 5;
          maxFunnelLimit = 5;
        } else {
          // Free
          maxWorkspaceLimit = -1;
          maxCampaignLimit = 1;
          maxMemberLimit = 1;
          maxFunnelLimit = 1;
        }
      }

      // Enforce globally unlimited workspaces limit to prevent UI issues
      maxWorkspaceLimit = -1;

      setWorkspaceLimit(maxWorkspaceLimit);
      setCampaignLimit(maxCampaignLimit);
      setMemberLimit(maxMemberLimit);
      setFunnelLimit(maxFunnelLimit);
      setHasAgencyPlan(userHasAgency);

      // 3. Set Active Workspace
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const user = session?.user;

      let profileDefaultWorkspaceId = null;
      if (user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("default_workspace_id")
          .eq("id", user.id)
          .maybeSingle();
        profileDefaultWorkspaceId = profile?.default_workspace_id;
        setDefaultWorkspaceId(profileDefaultWorkspaceId);
      }

      if (data && data.length > 0) {
        const defaultWorkspace = profileDefaultWorkspaceId
          ? data.find((ws) => String(ws.id) === String(profileDefaultWorkspaceId))
          : null;
        const workspaceToSelect = lastActiveId
          ? data.find((ws) => String(ws.id) === String(lastActiveId))
          : null;

        // Prefer selecting a workspace they own if no lastActiveId is found
        const firstOwnedWorkspace = user
          ? data.find((ws) => String(ws.owner_id) === String(user.id))
          : null;
        const selected =
          workspaceToSelect ||
          defaultWorkspace ||
          firstOwnedWorkspace ||
          data[0];

        setActiveWorkspace(selected);
        if (!workspaceToSelect) {
          localStorage.setItem("activeWorkspaceId", selected.id);
        }

        // Fetch subscription for the selected workspace
        await checkSubscription(selected);
      } else {
        // If no workspaces found, check if user is authenticated and create a default one
        if (user) {
          const isInvitedSetup =
            window.location.pathname.includes("/invited-setup");
          if (!isInvitedSetup) {
            // Only auto-create if we're sure the user is logged in and not in the middle of being invited
            console.log("No workspaces found. Creating default workspace...");
            // We await this so the UI updates after creation
            await addWorkspace("My Workspace", "Business");
          } else {
            console.log(
              "User is in invited setup. Skipping default workspace creation.",
            );
          }
        }
      }
    } catch (err) {
      // Ignore AbortError which can happen on rapid navigation or auth changes
      if (err.name === "AbortError" || err.message?.includes("AbortError")) {
        console.warn("Workspace fetch aborted");
      } else {
        console.error("Error fetching workspaces:", err);
        setError(err);
      }
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  const checkSubscription = async (workspace) => {
    if (!workspace) return;

    try {
      // Check if user is super admin to bypass checks
      const role = await getUserRole();
      if (
        (role === "super_admin" && adminOverrideEnabled) ||
        role === "lifetime"
      ) {
        setIsTrialExpired(false);
        setSubscription({
          status: "active",
          plan_name:
            role === "lifetime" ? "Lifetime Access" : adminSimulatedTier,
          amount: 0,
          current_period_end: new Date(
            Date.now() + 365 * 24 * 60 * 60 * 1000,
          ).toISOString(), // 1 year from now
        });
        return; // Bypass the rest
      }

      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("workspace_id", workspace.id)
        .limit(1)
        .maybeSingle();

      if (error && error.code !== "42P01") {
        console.error("Error fetching subscription:", error);
      }

      let subscriptionData = data;

      if (data && data.plan_name) {
        // Fetch plan details for gates
        const { data: planData } = await supabase
          .from("subscription_plans")
          .select("gates")
          .eq("name", data.plan_name)
          .maybeSingle();

        if (planData && planData.gates) {
          subscriptionData = { ...data, gates: planData.gates };
        }
      }

      setSubscription(subscriptionData);

      // Check for 7-day trial expiration
      // If no subscription or status is not active/trialing, check creation date
      const isActive =
        data && (data.status === "active" || data.status === "trialing");

      if (!isActive) {
        const createdAt = new Date(
          workspace.created_at || workspace.inserted_at || Date.now(),
        );
        const now = new Date();
        const diffTime = Math.abs(now - createdAt);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 7) {
          setIsTrialExpired(true);
        } else {
          setIsTrialExpired(false);
        }
      } else {
        setIsTrialExpired(false);
      }
    } catch (e) {
      console.error("Error checking subscription status:", e);
      // Default to safe state if check fails
      setIsTrialExpired(false);
    }
  };

  const switchWorkspace = (id) => {
    const ws = workspaces.find((w) => String(w.id) === String(id));
    if (ws) {
      setActiveWorkspace(ws);
      localStorage.setItem("activeWorkspaceId", id);
      checkSubscription(ws);
    }
  };

  const toggleViewMode = () => {
    const newMode = viewMode === "pro" ? "express" : "pro";
    setViewMode(newMode);
    localStorage.setItem("viewMode", newMode);
  };

  const toggleAdminOverride = () => {
    const newMode = !adminOverrideEnabled;
    setAdminOverrideEnabled(newMode);
    localStorage.setItem("adminOverrideEnabled", String(newMode));
  };

  const changeAdminSimulatedTier = (tier) => {
    setAdminSimulatedTier(tier);
    localStorage.setItem("adminSimulatedTier", tier);
  };

  const addWorkspace = async (name, type) => {
    if (error?.type === "MISSING_TABLES") {
      alert(
        "Cannot create workspace: Database tables are missing. Please check the README and run migrations.",
      );
      return;
    }

    // Soft warning at high counts (not a block)
    if (workspaces.length > 50) {
      // We'll log a warning, user can still proceed
      console.warn(
        "You have over 50 workspaces. Consider archiving inactive ones to keep your dashboard organized.",
      );
    }

    const colors = [
      "from-red-500 to-red-600",
      "from-indigo-500 to-indigo-600",
      "from-purple-500 to-purple-600",
      "from-orange-500 to-orange-600",
    ];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];

    try {
      // Get current user (use getSession for performance)
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const user = session?.user;

      if (!user) {
        alert("You must be logged in to create a workspace.");
        return;
      }

      const newWs = {
        name,
        type,
        logo: name.substring(0, 2).toUpperCase(),
        color: randomColor,
        owner_id: user.id,
      };

      const { data, error } = await supabase
        .from("workspaces")
        .insert([newWs])
        .select()
        .single();

      if (error) throw error;

      setWorkspaces([...workspaces, data]);
      setActiveWorkspace(data);

      // Sync user to Resend
      supabase.functions.invoke("sync-resend-contact").catch(console.error);
    } catch (err) {
      console.error("Error adding workspace:", err);
      // Check for 404 or missing table
      if (err.code === "42P01" || err.code === "PGRST205") {
        alert("Database setup incomplete. Please run migrations.");
      } else {
        alert(`Error creating workspace: ${err.message}`);
      }
    }
  };

  const updateWorkspace = async (id, updates) => {
    try {
      // Ensure we are working with a valid ID
      if (!id) throw new Error("Invalid Workspace ID");
      const cleanId = String(id).trim();

      const { data: result, error } = await supabase
        .from("workspaces")
        .update(updates)
        .eq("id", cleanId)
        .select("*");

      if (error) {
        // Enhance error logging for production debugging
        console.error(
          `[WorkspaceContext] Update failed for ID: ${cleanId}`,
          error,
        );
        if (error.code === "42501") {
          // RLS violation
          console.error(
            "[WorkspaceContext] RLS Permission Denied. Check user session and workspace ownership.",
          );
        }
        throw error;
      }

      // Robustly check for returned data
      const data =
        result && Array.isArray(result) && result.length > 0 ? result[0] : null;

      if (data) {
        setWorkspaces((prev) =>
          prev.map((ws) =>
            String(ws.id) === String(cleanId) ? { ...ws, ...data } : ws,
          ),
        );

        if (activeWorkspace && String(activeWorkspace.id) === String(cleanId)) {
          setActiveWorkspace((prev) => ({ ...prev, ...data }));
        }
      } else {
        console.warn(
          `[WorkspaceContext] Update returned no data for ID: ${cleanId}. This may indicate RLS filtering or a non-existent ID. Attempting refresh...`,
        );

        // Check if session is valid
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) {
          console.warn(
            "[WorkspaceContext] No active session found during update.",
          );
        }

        // Fallback: The mock environment might have issues returning the select() result correctly
        // in some specific chains. We force a refresh.
        await fetchWorkspaces(cleanId);
      }
    } catch (err) {
      console.error("Error updating workspace:", err);
      setError(err.message);
      throw err;
    }
  };

  const deleteWorkspace = async (id) => {
    try {
      // Invoke the edge function to handle both OpenRouter cleanup and database deletion
      const { data, error } = await supabase.functions.invoke(
        "delete-workspace",
        {
          body: { workspaceId: id },
        },
      );

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const newWorkspaces = workspaces.filter((ws) => String(ws.id) !== String(id));
      setWorkspaces(newWorkspaces);

      if (activeWorkspace && String(activeWorkspace.id) === String(id)) {
        if (newWorkspaces.length > 0) {
          const nextWorkspace = newWorkspaces[0];
          setActiveWorkspace(nextWorkspace);
          localStorage.setItem("activeWorkspaceId", nextWorkspace.id);
        } else {
          setActiveWorkspace(null);
          localStorage.removeItem("activeWorkspaceId");
        }
      }
    } catch (err) {
      console.error("Error deleting workspace:", err);
      setError(err.message);
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        Loading...
      </div>
    );
  }

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces,
        setWorkspaces,
        activeWorkspace,
        setActiveWorkspace,
        defaultWorkspaceId,
        setDefaultWorkspaceId,
        switchWorkspace,
        addWorkspace,
        updateWorkspace,
        deleteWorkspace,
        loading,
        error,
        workspaceLimit,
        campaignLimit,
        memberLimit,
        funnelLimit,
        workspacesLeft,
        subscription,
        isTrialExpired,
        viewMode,
        toggleViewMode,
        userRole,
        adminOverrideEnabled,
        toggleAdminOverride,
        adminSimulatedTier,
        changeAdminSimulatedTier,
        hasAgencyPlan,
        userFeatures,
        hasFeature,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};
